import React, { useState } from 'react';
import { apiUrl } from '../../utils/apiBase';

/**
 * Campos do fluxo Viagens (Dayane, controladoria — refeito em 29/10/2026).
 *
 * - "viajantes": lista de { id, nome, cpf }, com "+" para quantos viajarem. CPF obrigatório
 *   para todos (resposta da Dayane). O solicitante NÃO entra sozinho como viajante.
 * - "quadro_voos": quadro demonstrativo dos voos das cotações MSX (datas, horas, trechos,
 *   valor e tempo de escala/conexão). Desde 30/10/2026 o botão "Ler prints da MSX" (e a resposta
 *   do portal de cotação) lê os prints com o modelo de visão do Groq (/api/viagens/ler-prints-voos)
 *   e preenche uma linha por trecho; essas linhas levam `lido` (nome do print) até alguém editá-las.
 */

export interface Viajante { id: string; nome: string; cpf: string }

export interface LinhaVoo {
  id: string;
  cotacao: string;
  trecho: string;
  data: string;
  saida: string;
  chegada: string;
  voo: string;
  escalas: string;
  tempoEscala: string;
  valor: string;
  /** nome do print de onde a linha foi lida; some quando alguém edita a linha */
  lido?: string;
}

const novoId = (p: string) => `${p}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;

export const soDigitos = (s: any) => String(s ?? '').replace(/\D/g, '');

export const formatarCpf = (s: any) => {
  const d = soDigitos(s).slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
};

/** CPF com 11 dígitos e dígitos verificadores corretos. */
export const cpfValido = (s: any): boolean => {
  const d = soDigitos(s);
  if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false;
  const dv = (n: number) => {
    let soma = 0;
    for (let i = 0; i < n; i++) soma += Number(d[i]) * (n + 1 - i);
    const r = (soma * 10) % 11;
    return r === 10 ? 0 : r;
  };
  return dv(9) === Number(d[9]) && dv(10) === Number(d[10]);
};

export const normalizarViajantes = (v: any): Viajante[] =>
  Array.isArray(v)
    ? v.filter((x) => x && typeof x === 'object').map((x: any) => ({
        id: String(x.id || novoId('vj')), nome: String(x.nome ?? ''), cpf: String(x.cpf ?? ''),
      }))
    : [];

export const ehListaDeViajantes = (v: any) =>
  Array.isArray(v) && v.length > 0 && v.every((x: any) => x && typeof x === 'object' && 'cpf' in x && 'nome' in x);

export const textoViajantes = (v: any) =>
  normalizarViajantes(v).filter((x) => x.nome || x.cpf)
    .map((x) => `${x.nome || '?'}${x.cpf ? ` (CPF ${formatarCpf(x.cpf)})` : ''}`).join('; ');

/** Mensagem do que falta nos viajantes, ou null se está tudo certo. */
export const problemaViajantes = (v: any): string | null => {
  const lista = normalizarViajantes(v).filter((x) => x.nome.trim() || x.cpf.trim());
  if (lista.length === 0) return 'Inclua pelo menos um viajante (nome e CPF).';
  const semNome = lista.filter((x) => !x.nome.trim()).length;
  if (semNome) return 'Há viajante sem nome.';
  const ruins = lista.filter((x) => !cpfValido(x.cpf)).map((x) => x.nome.trim());
  if (ruins.length) return `CPF obrigatório e válido para todos os viajantes: confira ${ruins.join(', ')}.`;
  return null;
};

const entrada = 'w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-black focus:border-transparent bg-white';

export const ViajantesField: React.FC<{
  valor: any;
  onChange: (novo: Viajante[]) => void;
  disabled?: boolean;
}> = ({ valor, onChange, disabled }) => {
  const atuais = normalizarViajantes(valor);
  const lista = atuais.length ? atuais : [{ id: 'vj_vazio', nome: '', cpf: '' }];
  const gravar = (novos: Viajante[]) => onChange(novos);
  const alterar = (idx: number, k: 'nome' | 'cpf', v: string) => {
    const novos = lista.map((x) => ({ ...x, id: x.id === 'vj_vazio' ? novoId('vj') : x.id }));
    novos[idx] = { ...novos[idx], [k]: k === 'cpf' ? formatarCpf(v) : v };
    gravar(novos);
  };
  if (disabled) return <ViajantesResumo valor={valor} />;
  return (
    <div className="space-y-1.5">
      {lista.map((x, idx) => {
        const cpfRuim = !!soDigitos(x.cpf) && !cpfValido(x.cpf);
        return (
          // key pela posição: a 1ª linha nasce "vj_vazio" e ganha id na 1ª letra; com key={x.id} o input
          // era recriado e perdia o foco (Dayane, 30/10/2026). Os inputs são controlados, a posição basta.
          <div key={idx} className="flex items-center gap-1.5">
            <span className="text-[10px] font-semibold text-gray-500 w-10 flex-shrink-0">Nome:</span>
            <input value={x.nome} onChange={(e) => alterar(idx, 'nome', e.target.value)} placeholder="Nome completo" className={entrada} />
            <span className="text-[10px] font-semibold text-gray-500 w-8 flex-shrink-0 text-right">CPF:</span>
            <input value={formatarCpf(x.cpf)} onChange={(e) => alterar(idx, 'cpf', e.target.value)} placeholder="000.000.000-00"
              inputMode="numeric" className={`${entrada} max-w-[140px] ${cpfRuim ? 'border-red-400 bg-red-50' : ''}`}
              title={cpfRuim ? 'CPF inválido' : undefined} />
            <button type="button" onClick={() => gravar(lista.filter((_, i) => i !== idx))}
              className="text-red-400 hover:text-red-600 px-1 text-sm" title="Remover viajante" disabled={lista.length === 1 && !x.nome && !x.cpf}>✕</button>
          </div>
        );
      })}
      <button type="button" onClick={() => gravar([...lista.map((x) => ({ ...x, id: x.id === 'vj_vazio' ? novoId('vj') : x.id })), { id: novoId('vj'), nome: '', cpf: '' }])}
        className="w-full px-3 py-1.5 border-2 border-dashed border-blue-300 text-blue-600 hover:bg-blue-50 rounded-lg text-xs font-medium">
        + Adicionar viajante
      </button>
    </div>
  );
};

export const ViajantesResumo: React.FC<{ valor: any }> = ({ valor }) => {
  const lista = normalizarViajantes(valor).filter((x) => x.nome || x.cpf);
  if (!lista.length) return <span className="text-xs text-gray-400">—</span>;
  return (
    <div className="space-y-0.5">
      {lista.map((x) => (
        <div key={x.id} className="text-xs text-gray-800">
          <span className="font-medium">{x.nome || '?'}</span>
          <span className="text-gray-500"> · CPF {formatarCpf(x.cpf) || '—'}</span>
        </div>
      ))}
    </div>
  );
};

// ── Quadro demonstrativo dos voos ───────────────────────────────────────────

export const normalizarVoos = (v: any): LinhaVoo[] =>
  Array.isArray(v)
    ? v.filter((x) => x && typeof x === 'object').map((x: any) => ({
        id: String(x.id || novoId('voo')), cotacao: String(x.cotacao ?? ''), trecho: String(x.trecho ?? ''),
        data: String(x.data ?? ''), saida: String(x.saida ?? ''), chegada: String(x.chegada ?? ''),
        voo: String(x.voo ?? ''), escalas: String(x.escalas ?? ''), tempoEscala: String(x.tempoEscala ?? ''),
        valor: String(x.valor ?? ''),
        ...(x.lido ? { lido: String(x.lido) } : {}),
      }))
    : [];

export const ehListaDeVoos = (v: any) =>
  Array.isArray(v) && v.length > 0 && v.every((x: any) => x && typeof x === 'object' && 'trecho' in x && 'cotacao' in x);

const dataBr = (d: string) => {
  const m = String(d || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : d;
};

export const textoVoos = (v: any) =>
  normalizarVoos(v).map((x) => `${x.cotacao}: ${x.trecho} ${dataBr(x.data)} ${x.saida}-${x.chegada}${x.escalas ? ` (${x.escalas}${x.tempoEscala ? ` ${x.tempoEscala}` : ''})` : ''}${x.valor ? ` R$ ${x.valor}` : ''}`).join(' | ');

const COLUNAS: Array<[keyof LinhaVoo, string, string]> = [
  ['trecho', 'Trecho', 'FLN → GRU'],
  ['data', 'Data', ''],
  ['saida', 'Saída', '08:10'],
  ['chegada', 'Chegada', '10:45'],
  ['voo', 'Cia / voo', 'LATAM 3301'],
  ['escalas', 'Escalas / conexões', 'Conexão em GRU'],
  ['tempoEscala', 'Tempo de escala', '1h20'],
  ['valor', 'Valor (R$)', '1.234,56'],
];

export const QuadroVoosField: React.FC<{
  valor: any;
  onChange?: (novo: LinhaVoo[]) => void;
  disabled?: boolean;
  /** cotações que podem ter linhas (as MSX não excluídas) */
  opcoesCotacao?: string[];
  /** cotações escondidas (excluídas da aprovação) */
  excluidas?: string[];
  /** card aberto: habilita o botão "Ler prints da MSX" */
  cardId?: string;
  /** valores atuais da tela (prints recém-anexados ainda não salvos) */
  valoresCard?: Record<string, any>;
}> = ({ valor, onChange, disabled, opcoesCotacao = [], excluidas = [], cardId, valoresCard }) => {
  const todas = normalizarVoos(valor);
  const linhas = disabled ? todas.filter((x) => !excluidas.includes(x.cotacao)) : todas;
  const gravar = (novas: LinhaVoo[]) => onChange?.(novas);
  // editar a linha = conferida: deixa de ser "lida do print"
  const alterar = (id: string, k: keyof LinhaVoo, v: string) =>
    gravar(todas.map((x) => { if (x.id !== id) return x; const { lido: _l, ...resto } = x; return { ...resto, [k]: v }; }));
  const [lendo, setLendo] = useState(false);
  const [msgLeitura, setMsgLeitura] = useState<string | null>(null);

  const lerPrints = async () => {
    if (!cardId || lendo) return;
    setLendo(true); setMsgLeitura('Lendo os prints da MSX…');
    try {
      const res = await fetch(apiUrl('/viagens/ler-prints-voos'), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cardId, valores: valoresCard || {} }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j.success) { setMsgLeitura(`Não consegui ler: ${j.error || res.status}`); return; }
      const lidas: LinhaVoo[] = normalizarVoos(j.linhas || []);
      const cots: string[] = j.cotacoes || [];
      if (!cots.length) {
        setMsgLeitura((j.avisos || []).join(' · ') || 'Nenhum print de imagem nos campos de cotação da MSX.');
        return;
      }
      let resultado = [...todas];
      const feitas: string[] = [];
      cots.forEach((cot) => {
        const aMao = resultado.filter((x) => x.cotacao === cot && !x.lido);
        if (aMao.length && !window.confirm(
          `"${cot}" já tem ${aMao.length} linha(s) digitada(s) ou conferida(s) à mão no quadro.\n\n` +
          `OK = trocar pelas linhas lidas do print\nCancelar = manter as digitadas e não mexer nesta cotação`)) return;
        resultado = resultado.filter((x) => x.cotacao !== cot);
        resultado.push(...lidas.filter((x) => x.cotacao === cot));
        feitas.push(cot);
      });
      if (feitas.length) gravar(resultado);
      const n = lidas.filter((x) => feitas.includes(x.cotacao)).length;
      setMsgLeitura([feitas.length ? `${n} trecho(s) lidos de ${feitas.join(', ')} — confira e salve o card.` : 'Nada alterado.',
        ...(j.avisos || [])].join(' · '));
    } catch (e: any) {
      setMsgLeitura(`Não consegui ler: ${e?.message || e}`);
    } finally {
      setLendo(false);
    }
  };
  const opcoes = opcoesCotacao.filter((o) => !excluidas.includes(o));
  const grupos: Record<string, LinhaVoo[]> = {};
  linhas.forEach((x) => { (grupos[x.cotacao || 'Sem cotação'] = grupos[x.cotacao || 'Sem cotação'] || []).push(x); });

  if (disabled && linhas.length === 0) return <span className="text-xs text-gray-400">Nenhum voo lançado no quadro.</span>;

  return (
    <div className="space-y-2">
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full text-[11px]">
          <thead className="bg-slate-100 text-slate-600">
            <tr>
              <th className="px-2 py-1 text-left font-semibold">Cotação</th>
              {COLUNAS.map(([k, t]) => <th key={k} className="px-2 py-1 text-left font-semibold whitespace-nowrap">{t}</th>)}
              {!disabled && <th />}
            </tr>
          </thead>
          <tbody>
            {Object.entries(grupos).map(([cot, ls]) => ls.map((x, i) => {
              const temEscala = !!(x.escalas.trim() || x.tempoEscala.trim()) && !/^(direto|sem escala|nenhuma|-)$/i.test(x.escalas.trim());
              return (
                <tr key={x.id} className={`border-t border-gray-100 ${temEscala ? 'bg-amber-50' : 'bg-white'}`}>
                  <td className="px-2 py-1 font-semibold text-gray-700 whitespace-nowrap align-top">
                    {x.lido && <span className="mr-1 cursor-help" title={`Preenchido pela leitura do print ${x.lido} — confira`}>📷</span>}
                    {disabled ? (i === 0 ? cot : '') : (
                      <select value={x.cotacao} onChange={(e) => alterar(x.id, 'cotacao', e.target.value)} className="px-1 py-1 border border-gray-300 rounded text-[11px]">
                        <option value="">—</option>
                        {[...new Set([...opcoes, x.cotacao].filter(Boolean))].map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    )}
                  </td>
                  {COLUNAS.map(([k, , ph]) => (
                    <td key={k} className="px-1 py-1 align-top">
                      {disabled ? (
                        <span className={`whitespace-nowrap ${k === 'tempoEscala' || k === 'escalas' ? (temEscala ? 'text-amber-800 font-semibold' : 'text-gray-400') : 'text-gray-800'}`}>
                          {k === 'data' ? dataBr(x.data) : k === 'escalas' && !x.escalas ? 'Direto' : (x[k] || '—')}
                        </span>
                      ) : (
                        <input type={k === 'data' ? 'date' : 'text'} value={x[k]} placeholder={ph}
                          onChange={(e) => alterar(x.id, k, e.target.value)}
                          className={`px-1.5 py-1 border border-gray-300 rounded text-[11px] ${k === 'trecho' || k === 'escalas' ? 'w-32' : k === 'data' ? 'w-32' : 'w-20'}`} />
                      )}
                    </td>
                  ))}
                  {!disabled && (
                    <td className="px-1 py-1 align-top">
                      <button type="button" onClick={() => gravar(todas.filter((y) => y.id !== x.id))} className="text-red-400 hover:text-red-600 px-1" title="Remover linha">✕</button>
                    </td>
                  )}
                </tr>
              );
            }))}
            {linhas.length === 0 && (
              <tr><td colSpan={COLUNAS.length + 2} className="px-2 py-3 text-center text-gray-400">Nenhum voo lançado. Use "+ Voo" para transcrever as cotações recebidas.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {linhas.some((x) => x.escalas || x.tempoEscala) && (
        <p className="text-[10px] text-amber-700">Linhas em amarelo têm escala ou conexão.</p>
      )}
      {linhas.some((x) => x.lido) && (
        <p className="text-[10px] text-blue-700">📷 Preenchido pela leitura do print — confira (a linha perde o 📷 quando você edita).</p>
      )}
      {!disabled && cardId && (
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button" onClick={lerPrints} disabled={lendo}
            className="px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
            title="Lê os prints anexados nas Cotações MSX e preenche o quadro (uma linha por trecho)">
            {lendo ? 'Lendo prints…' : '📷 Ler prints da MSX'}
          </button>
          {msgLeitura && <span className="text-[11px] text-gray-600">{msgLeitura}</span>}
        </div>
      )}
      {!disabled && (
        <button type="button"
          onClick={() => {
            const ult = todas[todas.length - 1];
            gravar([...todas, { id: novoId('voo'), cotacao: ult?.cotacao || opcoes[0] || '', trecho: '', data: '', saida: '', chegada: '', voo: '', escalas: '', tempoEscala: '', valor: '' }]);
          }}
          className="w-full px-3 py-1.5 border-2 border-dashed border-blue-300 text-blue-600 hover:bg-blue-50 rounded-lg text-xs font-medium">
          + Voo
        </button>
      )}
    </div>
  );
};

// ── Diárias de alimentação pelo período da viagem ───────────────────────────

const CAMPOS_DATAS_LISTA = ['campo_mm9oxq1d_4t1fw0', 'campo_vg_roteiro_terrestre', 'campo_mmc1adba_18e2l8'];
const CAMPOS_DATAS_SIMPLES = ['campo_vg_carro_retirada_data', 'campo_vg_carro_entrega_data'];

/** Noites entre a primeira e a última data da viagem (mín. 1), igual ao _viagens_calcular do servidor. */
export const diariasDaViagem = (valores: Record<string, any>): number | null => {
  const datas: number[] = [];
  const add = (d: any) => {
    const m = String(d || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) datas.push(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  };
  CAMPOS_DATAS_LISTA.forEach((cid) => (Array.isArray(valores[cid]) ? valores[cid] : []).forEach((t: any) => t && add(t.data)));
  CAMPOS_DATAS_SIMPLES.forEach((cid) => add(valores[cid]));
  if (!datas.length) return null;
  return Math.max(Math.round((Math.max(...datas) - Math.min(...datas)) / 86400000), 1);
};

/** Necessidades pedem a diária Pluxee? */
export const pedePluxee = (valores: Record<string, any>) =>
  String(valores['campo_mm9ns7po_u01l04'] ?? '').toUpperCase().includes('PLUXEE');

// ── Viagem urgente (Dayane, 30/10/2026) ─────────────────────────────────────

/** Dias entre a data da solicitação (base) e a primeira data da viagem; null sem data. */
export const diasAteViagem = (valores: Record<string, any>, base?: any): number | null => {
  const datas: number[] = [];
  const add = (d: any) => {
    const m = String(d || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) datas.push(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  };
  CAMPOS_DATAS_LISTA.forEach((cid) => (Array.isArray(valores[cid]) ? valores[cid] : []).forEach((t: any) => t && add(t.data)));
  CAMPOS_DATAS_SIMPLES.forEach((cid) => add(valores[cid]));
  if (!datas.length) return null;
  // "2026-10-30" (data_solicitacao) vale como está; data com hora (createdAt) vai para o dia local
  const so = String(base || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  let baseUtc: number;
  if (so) baseUtc = Date.UTC(Number(so[1]), Number(so[2]) - 1, Number(so[3]));
  else {
    const b = base ? new Date(base) : new Date();
    const ref = isNaN(b.getTime()) ? new Date() : b;
    baseUtc = Date.UTC(ref.getFullYear(), ref.getMonth(), ref.getDate());
  }
  return Math.round((Math.min(...datas) - baseUtc) / 86400000);
};

/** Viagem com 20 dias ou menos entre a solicitação e a primeira data. */
export const viagemUrgente = (valores: Record<string, any>, base?: any): boolean => {
  const d = diasAteViagem(valores, base);
  return d != null && d <= 20;
};
